import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  SaveStockTransferRequest,
  StockTransferFilterParams,
  StockTransferLineRecord,
  StockTransferRecord,
  StockTransferSummaryCounts,
} from '../types/inventory';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

const toNumber = (value: unknown) => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapTransferLine = (
  v: Record<string, unknown>,
): StockTransferLineRecord => ({
  id: String(v.id || ''),
  stock_transfer_id: String(v.stock_transfer_id || ''),
  inventory_item_id: String(v.inventory_item_id || ''),
  quantity: toNumber(v.quantity),
  unit_cost: v.unit_cost === null ? null : toNumber(v.unit_cost),
  created_by: (v.created_by as string | null) ?? null,
  created_at: String(v.created_at || ''),
});

const mapTransfer = (v: Record<string, unknown>): StockTransferRecord => ({
  id: String(v.id || ''),
  company_id: toNumber(v.company_id),
  outlet_id: String(v.outlet_id || ''),
  from_stock_location_id: String(v.from_stock_location_id || ''),
  to_stock_location_id: String(v.to_stock_location_id || ''),
  transfer_date: String(v.transfer_date || ''),
  status: v.status as StockTransferRecord['status'],
  reference: (v.reference as string | null) ?? null,
  notes: (v.notes as string | null) ?? null,
  posted_at: (v.posted_at as string | null) ?? null,
  posted_by: (v.posted_by as string | null) ?? null,
  created_by: (v.created_by as string | null) ?? null,
  created_at: String(v.created_at || ''),
  updated_at: String(v.updated_at || ''),
  lines: Array.isArray(v.inventory_stock_transfer_lines)
    ? (v.inventory_stock_transfer_lines as Record<string, unknown>[]).map(
        mapTransferLine,
      )
    : undefined,
});

export async function fetchStockTransfers(
  filters: StockTransferFilterParams,
): Promise<PaginatedResponse<StockTransferRecord>> {
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('inventory_stock_transfers')
    .select('*', { count: 'exact' })
    .eq('company_id', filters.companyId)
    .eq('outlet_id', filters.outletId)
    .order('transfer_date', { ascending: false })
    .range(from, to);

  if (filters.status && filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }
  if (filters.dateFrom) query = query.gte('transfer_date', filters.dateFrom);
  if (filters.dateTo) query = query.lte('transfer_date', filters.dateTo);
  if (filters.search) {
    query = query.or(
      `reference.ilike.%${filters.search}%,notes.ilike.%${filters.search}%`,
    );
  }

  const { data, error, count } = await query;
  if (error) throw error;

  const totalPages = Math.ceil((count ?? 0) / pageSize);
  return {
    data: (data ?? []).map((r) => mapTransfer(r as Record<string, unknown>)),
    pagination: {
      page,
      pageSize,
      totalItems: count ?? 0,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    },
  };
}

export async function fetchStockTransferDetail(
  transferId: string,
): Promise<StockTransferRecord | null> {
  const { data, error } = await supabase
    .from('inventory_stock_transfers')
    .select('*, inventory_stock_transfer_lines(*)')
    .eq('id', transferId)
    .single();

  if (error) throw error;
  if (!data) return null;
  return mapTransfer(data as Record<string, unknown>);
}

export async function fetchStockTransferSummary(
  companyId: number,
  outletId: string,
): Promise<StockTransferSummaryCounts> {
  const { data, error } = await supabase
    .from('inventory_stock_transfers')
    .select('status')
    .eq('company_id', companyId)
    .eq('outlet_id', outletId);

  if (error) throw error;

  const rows = data ?? [];
  return {
    totalTransfers: rows.length,
    draftTransfers: rows.filter((r) => r.status === 'draft').length,
    postedTransfers: rows.filter((r) => r.status === 'posted').length,
  };
}

export async function saveStockTransfer(
  req: SaveStockTransferRequest,
): Promise<ApiResponse<StockTransferRecord>> {
  const today = new Date().toISOString().split('T')[0];

  if (req.id) {
    const { data: existing } = await supabase
      .from('inventory_stock_transfers')
      .select('status')
      .eq('id', req.id)
      .single();
    if (existing?.status !== 'draft') {
      return { success: false, error: 'Only draft transfers can be edited' };
    }

    const { error: headerError } = await supabase
      .from('inventory_stock_transfers')
      .update({
        from_stock_location_id: req.fromStockLocationId,
        to_stock_location_id: req.toStockLocationId,
        transfer_date: req.transferDate ?? today,
        reference: req.reference ?? null,
        notes: req.notes ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', req.id);

    if (headerError) return { success: false, error: headerError.message };

    await supabase
      .from('inventory_stock_transfer_lines')
      .delete()
      .eq('stock_transfer_id', req.id);

    if (req.lines.length > 0) {
      const { error: linesError } = await supabase
        .from('inventory_stock_transfer_lines')
        .insert(
          req.lines.map((l) => ({
            stock_transfer_id: req.id,
            inventory_item_id: l.inventoryItemId,
            quantity: l.quantity,
            unit_cost: l.unitCost ?? null,
          })),
        );
      if (linesError) return { success: false, error: linesError.message };
    }

    const updated = await fetchStockTransferDetail(req.id);
    return { success: true, data: updated ?? undefined };
  }

  const { data: header, error: headerError } = await supabase
    .from('inventory_stock_transfers')
    .insert({
      company_id: req.companyId,
      outlet_id: req.outletId,
      from_stock_location_id: req.fromStockLocationId,
      to_stock_location_id: req.toStockLocationId,
      transfer_date: req.transferDate ?? today,
      reference: req.reference ?? null,
      notes: req.notes ?? null,
    })
    .select()
    .single();

  if (headerError) return { success: false, error: headerError.message };

  const transferId = (header as Record<string, unknown>).id as string;

  if (req.lines.length > 0) {
    const { error: linesError } = await supabase
      .from('inventory_stock_transfer_lines')
      .insert(
        req.lines.map((l) => ({
          stock_transfer_id: transferId,
          inventory_item_id: l.inventoryItemId,
          quantity: l.quantity,
          unit_cost: l.unitCost ?? null,
        })),
      );
    if (linesError) return { success: false, error: linesError.message };
  }

  const created = await fetchStockTransferDetail(transferId);
  return { success: true, data: created ?? undefined };
}

export async function postStockTransfer(
  transferId: string,
): Promise<ApiResponse<{ transferId: string; linesProcessed: number }>> {
  const { data, error } = await supabase.rpc('post_stock_transfer', {
    p_transfer_id: transferId,
  });

  if (error) return { success: false, error: error.message };

  const result = data as Record<string, unknown>;
  if (!result.success) {
    return { success: false, error: String(result.error ?? 'Post failed') };
  }

  return {
    success: true,
    data: {
      transferId: String(result.transfer_id),
      linesProcessed: Number(result.lines_processed ?? 0),
    },
  };
}
